// 主线程轮询管理器，统一管理 Worker 和订阅关系
import { trackerHandler } from './tracker';

let worker = null; // Web Worker 实例（单例）
const subscribers = new Map(); // 订阅者映射：key => Set(callbacks)
const componentSubscriptions = new Map(); // url => unsubscribeFn

const subscribersVersionCheck = new Map(); // 订阅者映射：key => Set(callbacks)

/**
 * 生成唯一订阅 key：url + 参数哈希
 * @param {string} url
 * @param {Object} data - 请求参数
 * @returns {string} 唯一 key
 */
function generateKey(url, data) {
  // 简易哈希（生产环境可用更稳定的 hash，如 murmurhash）
  const str = url + JSON.stringify(data || {});
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // 转为32位整数
  }
  return `${url}__${hash}`;
}

/**
 * 初始化 Web Worker（只创建一次）
 */
function initWorker() {
  if (worker) return;
  // 使用 import.meta.url 确保路径正确（支持 Vite/Webpack）
  worker = new Worker(new URL('./polling.js', import.meta.url));

  // 监听 Worker 消息，并分发给订阅者
  worker.onmessage = (e) => {
    const msg = e.data;
    if(msg.type === 'track-event') {
      trackerHandler(msg);
    }
    else if(msg.type === 'polling') {
      const { status, key, result, error } = msg;
      const callbacks = subscribers.get(key);
      if (!callbacks) return; // 无人订阅，忽略
      
      callbacks.forEach(cb => {
        if (status === 'success') cb(null, result);
        else cb(error, null); // (error, data) 风格
      });
    }
    else if(msg.type === 'version-check') {
      const { taskId, newVersion, oldVersion } = msg;
      const callbacks = subscribersVersionCheck.get(taskId);
      if (!callbacks) return;

      // 版本检查完成，触发回调
      callbacks.forEach(cb => {
        cb(null, { newVersion, oldVersion });
      });
    }
  };
  worker.onerror = (e) => {
    console.error('worker error', e);
  };
}

/**
 * 订阅某个接口的轮询数据
 * @param {string} url - 接口路径
 * @param {Object} config - 轮询配置（含 token、baseURL、interval 等）
 * @param {Function} callback - 回调 (error, data) => void
 */
export function subscribePolling(url, config, callback) {
  initWorker();
  // 如果未登录，直接不往下执行
  if(!config.token) return;
  // 生成唯一 key（含参数）
  const key = generateKey(url, config.data);

  // 检查是否已存在相同 URL 的订阅，先取消旧的
  if (componentSubscriptions.has(url)) {
    const oldUnsub = componentSubscriptions.get(url);
    oldUnsub(); // 取消旧订阅
  }

  if (!subscribers.has(key)) {
    // 首次订阅：创建订阅集合，并启动轮询
    subscribers.set(key, new Set());
    console.log('定义轮询入参', { url, ...config });
    worker.postMessage({ type: 'polling', action: 'start', key, config: { url, ...config } });
  }
  // 添加当前回调到订阅者列表
  // 检查回调是否已存在，避免重复添加
  const callbackSet = subscribers.get(key);
  if (!callbackSet.has(callback)) {
    callbackSet.add(callback);
  }

  // 创建新的取消函数
  const unsubscribeFn = () => {
    unsubscribePolling(key, callback);
    // 从组件订阅管理中移除
    componentSubscriptions.delete(url);
  };

  // 记录到组件订阅管理中（用 URL 作为标识）
  componentSubscriptions.set(url, unsubscribeFn);

  // 返回取消函数（关键！）
  return unsubscribeFn;
}

/**
 * 取消订阅
 * @param {string} key - 接口路径
 * @param {Function} callback - 原订阅回调
 */
export function unsubscribePolling(key, callback) {
  const callbacks = subscribers.get(key);
  if (!callbacks) return;
  callbacks.delete(callback);

  // 若无人订阅，停止该接口轮询
  if (callbacks.size === 0) {
    subscribers.delete(key);
    worker.postMessage({ type: 'polling', action: 'stop', key });
  }
}

/**
 * 全局销毁所有接口轮询（用于 token 过期、退出登录等场景）
 */
export function destroyAllPolling() {
  // 不能终止 worker，否则无法检查版本更新
  // if (worker) {
  //   worker.terminate();
  //   worker = null;
  // }
  
  for (const unsub of componentSubscriptions.values()) {
    unsub?.(); // 可选链写法（ES2020+）
  }
  componentSubscriptions.clear();
  subscribers.clear();
}

// === 版本检查相关函数 ===
export function startVersionCheck(config, callback) {
  initWorker();
  const taskId = `version-check__${Date.now()}`;

  if (!subscribersVersionCheck.has(taskId)) {
    subscribersVersionCheck.set(taskId, new Set());
    worker.postMessage({ type: 'version-check', action: 'start', taskId, config });
  }

  // 添加去重检查
  const callbackSet = subscribersVersionCheck.get(taskId);
  if (!callbackSet.has(callback)) {
    callbackSet.add(callback);
  }

  // 返回停止函数
  return () => {
    const callbacks = subscribersVersionCheck.get(taskId);
    if (!callbacks) return;
    callbacks.delete(callback);
    if (callbacks.size === 0) {
      subscribersVersionCheck.delete(taskId);
      worker.postMessage({ type: 'version-check', action: 'stop', taskId });
    }
  };
}
